import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, FileText, Users, TrendingUp, Clock, Shield, MessageCircle, ChevronRight, CreditCard } from "lucide-react";
import ContactForm from "../components/ContactForm";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20Personal%20Loan%20Assistance%20in%20Coimbatore";

const eligibility = [
  {
    title: "Salaried Employees (Private & Government)",
    desc: "Full-time employees with a minimum of 1 year of work experience and 6 months in the current organization. Both private sector and government employees are eligible.",
  },
  {
    title: "Self-Employed Professionals",
    desc: "Doctors, CAs, lawyers, architects, and other licensed professionals with a stable practice and minimum 2 years of business continuity in Coimbatore or Tamil Nadu.",
  },
  {
    title: "Business Owners",
    desc: "Proprietors, partners, and directors of businesses with a proven track record, regular income, and established banking relationships. GST-registered businesses preferred.",
  },
  {
    title: "Individuals with Regular Income",
    desc: "Freelancers, consultants, and other individuals with demonstrable regular income through bank statements. Income stability is the key factor for loan approval.",
  },
];

const documents = [
  "PAN Card (mandatory for all loan applications)",
  "Aadhaar Card (for identity and address verification)",
  "Salary Slips (last 3 to 6 months) — for salaried applicants",
  "Bank Statements (last 6 months showing salary credits or business income)",
  "Income Proof for Self-Employed (ITR for 2 years, Profit & Loss statement, Balance Sheet)",
  "Employment/Business Proof (offer letter, appointment letter, or business registration certificate)",
  "Passport-size photographs",
];

const processSteps = [
  { number: "01", title: "Submit Enquiry", desc: "Reach us via the contact form, WhatsApp, or phone with your loan requirement, income details, and preferred loan amount." },
  { number: "02", title: "Eligibility Check", desc: "Our team evaluates your income, employment type, CIBIL score, and existing obligations to determine your loan eligibility and best options." },
  { number: "03", title: "Document Verification", desc: "We review and organize all your documents, ensuring they meet lender requirements. We flag and help resolve any issues before submission." },
  { number: "04", title: "Application Coordination", desc: "We prepare and submit your application to the most suitable banks and NBFCs, ensuring the best interest rates and terms for your profile." },
  { number: "05", title: "Follow-Up with Lender", desc: "Dedicated follow-up with the lending institution from application to approval and disbursement. We keep you updated at every step." },
];

const approvalFactors = [
  {
    title: "CIBIL Score",
    desc: "Your credit score is the most critical factor. A CIBIL score of 750+ significantly improves approval chances and helps secure lower interest rates. Scores between 650-750 are acceptable but may attract higher rates.",
    icon: <TrendingUp size={24} />,
  },
  {
    title: "Income Stability",
    desc: "Lenders assess your monthly income and its consistency. Salaried individuals need a minimum net monthly income (typically Rs. 15,000-25,000). Self-employed applicants need to show consistent income over 2+ years.",
    icon: <CreditCard size={24} />,
  },
  {
    title: "Existing EMIs",
    desc: "Your debt-to-income ratio matters. If your existing EMIs consume more than 40-50% of your monthly income, lenders may reduce the eligible loan amount or reject the application.",
    icon: <FileText size={24} />,
  },
  {
    title: "Employment Type & Tenure",
    desc: "Permanent employees in reputed companies get preferential treatment. Government employees often get the best rates. Self-employed applicants need longer business vintage (2-3 years minimum).",
    icon: <Users size={24} />,
  },
];

const faqs = [
  {
    question: "What is the minimum salary required for personal loan in Coimbatore?",
    answer: "The minimum salary requirement varies by lender. Most banks require a minimum net monthly salary of Rs. 15,000-25,000 for personal loan eligibility. Some NBFCs offer loans for salaries as low as Rs. 12,000. Our team will match you with the right lender based on your salary level.",
  },
  {
    question: "How long does personal loan approval take?",
    answer: "Personal loan approval typically takes 2-7 working days from the date of complete document submission. Some banks offer instant approval for pre-approved customers. With WINTRUST's documentation support, we ensure zero delays from incomplete paperwork, helping you get faster processing.",
  },
  {
    question: "Can self-employed individuals apply for personal loan in Coimbatore?",
    answer: "Yes, self-employed individuals including business owners, freelancers, and professionals can apply for personal loans. You'll need to provide ITR for the last 2 years, bank statements showing regular income, and business registration proof. Interest rates may be slightly higher compared to salaried applicants.",
  },
  {
    question: "What CIBIL score is required for personal loan?",
    answer: "A CIBIL score of 750 or above is ideal for personal loan approval with the best interest rates. Scores between 650-750 are still eligible but may attract higher rates. Below 650, approval becomes difficult with mainstream banks, though some NBFCs may still consider your application.",
  },
  {
    question: "What is the maximum personal loan amount available?",
    answer: "The maximum personal loan amount depends on your income, credit score, and lender policies. Generally, salaried individuals can get up to 10-15 times their monthly salary, with a maximum of Rs. 25-40 lakhs from most banks. High-income professionals may qualify for up to Rs. 50 lakhs or more.",
  },
  {
    question: "Is collateral required for personal loans?",
    answer: "No, personal loans are unsecured loans and do not require any collateral or security. Approval is based entirely on your income, credit score, and repayment capacity. This makes personal loans ideal for those who don't have assets to pledge but need quick funding.",
  },
];

const PersonalLoanPage = () => {
  useCanonical("/personal-loan-assistance");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Personal Loan Assistance in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Expert personal loan assistance for salaried and self-employed individuals in Coimbatore. Documentation support, eligibility assessment, and bank coordination.",
  });

  return (
    <>
      <Helmet>
        <title>Personal Loan Assistance in Coimbatore | WinTrust</title>
        <meta name="description" content="Get expert personal loan assistance in Coimbatore for salaried & self-employed individuals. Quick guidance, documentation support & faster processing." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-16 md:py-20">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-6">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Personal Loan Assistance</span>
          </nav>
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-6" data-testid="personal-loan-h1">
              Personal Loan Assistance in Coimbatore – Fast & Reliable Support
            </h1>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              Need a personal loan but unsure where to start? At WINTRUST SOLUTIONS, we provide expert <strong className="text-white">personal loan assistance in Coimbatore</strong> for both salaried and self-employed individuals. Whether you need funds for a medical emergency, home renovation, wedding, education, or debt consolidation, our team guides you through every step. We help you <strong className="text-white">apply for a personal loan in Coimbatore</strong> with confidence — from eligibility assessment and documentation support to coordinating with banks and NBFCs for the best rates. Our transparent advisory approach ensures you understand all terms, fees, and repayment options before committing. With dedicated <strong className="text-white">salaried personal loan</strong> and <strong className="text-white">self-employed loan support in Coimbatore</strong>, we make borrowing simple, fast, and stress-free.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary flex items-center gap-2" data-testid="hero-apply-btn">
                Apply Now <ChevronRight size={18} />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={18} />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Apply */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Who Can Apply for Personal Loan in Coimbatore?
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Personal loans are available to a wide range of individuals in Coimbatore and across Tamil Nadu. Here's who qualifies:
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {eligibility.map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl border border-slate-200" data-testid={`eligibility-${i}`}>
                <CheckCircle className="text-gold flex-shrink-0 mt-1" size={22} />
                <div>
                  <h3 className="font-semibold text-navy font-heading">{item.title}</h3>
                  <p className="text-slate-600 text-sm mt-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
              Documents Required for Personal Loan
            </h2>
            <p className="text-slate-600 text-center mb-10">
              Keep these documents ready for a smooth and quick personal loan application in Coimbatore:
            </p>
            <div className="space-y-3">
              {documents.map((doc, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                  <FileText className="text-gold flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-navy text-sm">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Our Personal Loan Assistance Process
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Our simple 5-step process ensures quick and hassle-free personal loan assistance in Coimbatore.
          </p>
          <div className="max-w-3xl mx-auto space-y-6">
            {processSteps.map((step, i) => (
              <div key={i} className="flex gap-5 items-start" data-testid={`process-step-${i}`}>
                <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-gold font-bold text-sm">{step.number}</span>
                </div>
                <div className="pt-1">
                  <h3 className="text-lg font-semibold text-navy font-heading">{step.title}</h3>
                  <p className="text-slate-600 text-sm mt-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Factors Affecting Approval */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Factors That Affect Personal Loan Approval
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Understanding these factors can help you improve your chances of getting approved for a personal loan in Coimbatore.
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {approvalFactors.map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-white rounded-xl border border-slate-200" data-testid={`factor-${i}`}>
                <div className="w-12 h-12 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0 text-gold">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-navy mb-1 font-heading">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Why Choose WinTrust Solutions in Coimbatore?
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Your trusted partner for personal loan assistance in Coimbatore and Tamil Nadu.
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { icon: <Clock size={24} />, title: "Quick Response Time", desc: "We respond to every enquiry within hours, not days. Get an initial eligibility assessment within 24 hours of contacting us." },
              { icon: <Users size={24} />, title: "Dedicated Advisory Support", desc: "A single point of contact handles your entire loan journey. No bouncing between departments or repeating your details." },
              { icon: <FileText size={24} />, title: "End-to-End Documentation Guidance", desc: "We help you collect, organize, and submit every document correctly the first time. No rejections due to paperwork issues." },
              { icon: <Shield size={24} />, title: "Transparent Communication", desc: "We explain all charges, interest rates, processing fees, and terms upfront. No hidden costs, no surprises after disbursement." },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-slate-50 rounded-xl border border-slate-200">
                <div className="w-12 h-12 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0 text-gold">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-navy mb-1 font-heading">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-navy mb-4 font-heading">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Common questions about personal loan assistance in Coimbatore
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg border border-slate-200 px-6"
                  data-testid={`loan-faq-${index}`}
                >
                  <AccordionTrigger className="text-left text-navy font-medium py-4 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Cross-link to Business Loan */}
      <section className="bg-white py-10">
        <div className="container-custom text-center">
          <p className="text-slate-600 mb-3">Looking for a business loan instead?</p>
          <Link to="/business-loan-assistance" className="text-gold font-semibold hover:underline inline-flex items-center gap-1">
            Business Loan Assistance in Coimbatore <ChevronRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-navy section-padding">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 font-heading">
            Apply for Personal Loan in Coimbatore Today
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Get expert guidance for personal loans — whether you're salaried, self-employed, or a professional. Our Coimbatore team is ready to help you secure the right loan at the best rates.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#contact" className="btn-primary flex items-center gap-2" data-testid="cta-apply-btn">
              Contact Us Now <ChevronRight size={18} />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <MessageCircle size={18} />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />
    </>
  );
};

export default PersonalLoanPage;
